import * as React from 'react';
import classnames from 'classnames';

import { Res } from 'core/api';
import { Binding, Result } from 'core/infrastructure';

import { Constraint, check } from './Constraints';
import ErrorBlock from './ErrorBlock';
import { FormComponent } from './Form';
import { formatAsElementId } from './Utils';
import { DateUtils } from 'core/utils';

interface Props<T> extends React.HTMLProps<HTMLInputElement> {
	linkTo: Binding.PropertyPath<T>;
	label?: string;
	boundValue?: Nullable<Date>;
	rules?: Constraint | Constraint[];
	formId?: string;
	onValueChanged?: (date: Nullable<Date>) => void;
}

interface State {
	value: Nullable<Date>;
	errorMsg: string;
}

export class NativeDatePicker<T> extends React.Component<Props<T>, State> implements FormComponent {

	constructor(props: Props<T>) {
		super(props);

		const isNullableDate = (props.boundValue instanceof Date) || props.boundValue === null;
		if (!isNullableDate)
			throw new Error('NativeDatePicker esperaba un bound value de tipo Nullable<Date>');

		this.state = { value: props.boundValue || null, errorMsg: '' };
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<Nullable<Date>> {

		this.setState({ errorMsg: '' });

		const value = this.state.value;
		const rulesError = this.props.rules && check(this.props.rules, value);

		if (this.props.required && !value) {
			this.setState({ errorMsg: Res.Msg.MSG_VALUE_REQUIRED() });
			return Result.error();
		}

		if (rulesError) {
			this.setState({ errorMsg: rulesError });
			return Result.error();
		}

		return Result.ok(value);
	}

	render() {

		const { boundValue, linkTo, label, formId, onValueChanged, rules, ref, className, style, ...inputProps } = this.props;

		const inputId = formatAsElementId(this.getPath(), formId || '');

		const onInput = (e: React.FormEvent<HTMLInputElement>) => {
			// Necesario porque en iOS el botón para borrar la fecha lo que hace es restablecer al defaultValue,
			// y como esto es un controlled component, react establece a la vez value y defaultValue al mismo valor
			// (lo que hay en this.state.value), y rompe el comportamiento del datepicker
			// https://github.com/facebook/react/issues/8938#issuecomment-360573204
			const target = e.nativeEvent.target as HTMLInputElement;
			function iosClearDefault() { target.defaultValue = ''; }
			setTimeout(iosClearDefault, 0);
		};

		const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const date = e.target.value ? DateUtils.parseDate(e.target.value, 'yyyy-MM-dd') : null;
			this.setState({ value: date }, () => onValueChanged?.(date));
		};

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			{
				label
					? <label className='col-sm-4 control-label' htmlFor={inputId}>
						{label || ''}
					</label>
					: null
			}

			<div className={classnames({ 'col-sm-8': !!label, 'col-sm-12': !label })}>
				<input
					id={inputId}
					type='date'
					className={`${className ?? ''} form-control`}
					value={this.state.value ? DateUtils.formatDate(this.state.value, 'yyyy-MM-dd') : ''}
					onChange={onChange}
					onInput={onInput}
					{...inputProps} />
				<ErrorBlock msg={this.state.errorMsg} />
			</div>

		</div>;
	}
}


export type NativeDatePickerCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
